import { env } from '../../config';
import { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import Switch from "react-switch";
import { ReactSVG } from 'react-svg';
import { formatDate, getLocationsByProduct, isAFSlide, userHasPermission, getCommonLocationsBetweenUserPermsAndProduct } from '../../utils';
import { getPlaylistData, createOrUpdatePlaylistSlide, saveContentPlaylist, removePlaylist, createPlaylist, setLoopDuration, getSlideById, capitalizeFirst, truncateLabel } from './controller';
import { replaceUnderscore } from '../ContentLibraryModal/utils';

import transitTile from '../../images/content-library/transitscreen_tile.png';
import nearbyTile from '../../images/content-library/nearby_tile.png';
import landscapeIcon from '../../images/icons/screens/landscape.svg';
import portraitIcon from '../../images/icons/screens/portrait-left.svg';
import mobileIcon from '../../images/icons/screens/mobile.svg';
import dragIcon from '../../images/icons/contentManagement/drag-icon.svg'
import errorIcon from '../../images/icons/contentManagement/error-icon.svg';
import deleteIcon from '../../images/icons/analytics/trash.svg';
import disabledDeleteIcon from '../../images/icons/analytics/trash-disabled.svg';
import openReport from '../../images/icons/analytics/open-report.svg';
import plusIcon from '../../images/icons/plus-sign.svg';
import linkIcon from '../../images/icons/link.svg';
import upCaret from '../../images/icons/up-caret.svg';
import downCaret from '../../images/icons/down-caret.svg';
import returnIcon from '../../images/icons/screens/return.svg';
import saveIcon from '../../images/icons/screens/save.svg';
import eyeIcon from '../../images/icons/screens/eye.svg';
import questionIcon from '../../images/icons/contentManagement/question.svg';

const PLAYLIST_SLIDE_NUMBER = 10;
const DURATION_OPTIONS = [
	{label: '3 sec', value: 3000},
	{label: '5 sec', value: 5000},
	{label: '10 sec (default)', value: 10000},
	{label: '15 sec', value: 15000},
	{label: '30 sec', value: 30000},
	{label: '60 sec', value: 60000},
	{label: 'Permanent', value: null, disabled:true},
];
const DEVICE_TYPE_OPTIONS = [
  { value: 'landscape', rotation: 0, icon: landscapeIcon, label: 'Screen - Landscape (default)' },
  { value: 'portrait', rotation: 90, icon: portraitIcon, label: 'Screen - Portrait' },
  { value: 'mobile', rotation: 0, icon: mobileIcon, label: 'Mobile' },
];

const ContentManagementPage = ({
	userData,
	customer,
	handleContentModalOpen,
	permissions
}) => {

	const [playlist, setPlaylist] = useState({});
	const [loop, setLoop] = useState(true);
	const [initialLoop, setInitialLoop] = useState(false);
	const [showOpenPlaylistModal, setShowOpenPlaylistModal] = useState(false);
	const [locations, setLocations] = useState([]);
	const [playlistSlides, setPlaylistSlides] = useState([]);
	const [initialPlaylistSlides, setInitialPlaylistSlides] = useState([]);
	const [initialPlaylist, setInitialPlaylist] = useState({});
	const [stateChanged, setStateChanged] = useState(false);
	const selectedIndexRef = useRef(null);

	const [playlists, setPlaylists] = useState([]);
	const [showPlaylistModal, setShowPlaylistModal] = useState(false);
	const [message, setMessage] = useState('');
	const [enabledButtons, setEnabledButtons] = useState(false);

	const [showSaveMessageModal, setShowSaveMessageModal] = useState(false);
	const [showErrorMessageModal, setShowErrorMessageModal] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [showWarningMessageModal, setShowWarningMessageModal] = useState(false);
	const [warningMessage, setWarningMessage] = useState('');
	const warningCallbackRef = useRef();

	const [deletedSlides, setDeletedSlides] = useState([]);

	const [showConfirmLocationChangeModal, setShowConfirmLocationChangeModal] = useState(false);
	const [confirmLocationChangeMessage, setConfirmLocationChangeMessage] = useState('');

	useEffect(() => {
		// Only look for specific user permissions if not an admin
		getLocationsByProduct(customer.id, userData.role === 'af_admin' ? null : userData.sub, 'playlistFull')
		.then(locationsData => {
			setLocations(locationsData);
		});
	}, []);

	useEffect(() => {
		getPlaylists();
	}, []);

	const getPlaylists = () => {
		getPlaylistData(customer.id, permissions['playlistFull'])
		.then(data => {
			setPlaylists(data)
		})
		.catch((error) => {
			console.error('Something went wrong:', error);
		});

		return;
	}

	// useEffect(() => {
	// 	updatePlaylistAndSlideDefinitions();
	// }, [deletedSlides]);

	const updatePlaylistAndSlideDefinitions = () => {
		getPlaylistData(customer.id, permissions['playlistFull'])
		.then(data => {
			//Find the BIZ playlist to update current DASH2 data by
			const updatedPlaylist = data.find(d => d.playlist.id === playlist?.id);
			const updatedPlaylistSlides = updatedPlaylist?.playlistSlides;
			if (updatedPlaylistSlides) {
				// Sort
				updatedPlaylistSlides.sort((a, b) => {
					return Number(a.sortOrder) - Number(b.sortOrder);
				});

				setPlaylistSlides(updatedPlaylistSlides);
				setPlaylists(data);
				setEnabledButtons(false);
			}
		})
		.catch((error) => {
			console.error('Something went wrong:', error);
			// I don't know what state the playlist is in, but make sure the buttons are activated
			setEnabledButtons(true);
		});
	}

	const fillSvgColor = (svg, stroke, fill) => {
		if (stroke || fill) {
			for (var i = 0; i < svg.children.length; i++) {
                svg.children[i].setAttribute('stroke', stroke);
				svg.children[i].setAttribute('fill', fill);
            }
		}
	}

	const getLinkTile = (slide, classList = '') => {
		let prefix = classList.includes('draggable-link') ? 'draggable-link' : 'droppable-link';
		return <div className={classList}>
			<div className="draggable-link--link-icon"><img src={linkIcon} height="45px" /></div>
			<div className={`proxima-bold ${prefix}--label`}>{slide.label}</div>
		</div>
	}
	
	const selectPlaylist = (value) => {
		
		let tempPlaylistSlides = [...value.playlistSlides];

		// Sort by sortOrder ascending
		tempPlaylistSlides.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

		const lastSlide = tempPlaylistSlides.findLast(ps => ps);
		//Check if empty playlist
		if (!lastSlide) {
			setLoop(true);
			setInitialLoop(true);
		}
		//Check if this is a loop or not
		else if (!lastSlide?.durationMs) {
			setLoop(false);
			setInitialLoop(false);
		}
		else {
			setLoop(true);
			setInitialLoop(true);
		}
		
		setStateChanged(false);
		setPlaylist(value.playlist);
		setInitialPlaylist(value.playlist);
		setPlaylistSlides(tempPlaylistSlides);
		//Preparing for user clicking the Undo button
		setInitialPlaylistSlides(tempPlaylistSlides);
		// New playlist, therefore no changes yet.
		setEnabledButtons(false);
	}

	const handleDescriptionChange = (value) => {
		 setPlaylist({ ...playlist, label: value });
		 setStateChanged(true);
	}

	const selectDurationMs = (val, index) => {
		let psArr = [...playlistSlides];
		psArr[index].durationMs = val;
		setPlaylistSlides(psArr)
		setEnabledButtons(true);
		setStateChanged(true);
	}

	const setCountdownIndicator = (val, index) => {
		let psArr = [...playlistSlides];
		psArr[index].showTimer = val;
		setPlaylistSlides(psArr);
		setEnabledButtons(true);
		setStateChanged(true);
	}

	const displayDurationOption = (durationMs) => {
		const label = DURATION_OPTIONS.find(option => option.value === durationMs)?.label;
		return {
			label,
			value: durationMs
		}
	}

	const confirmUndoChanges = () => {
		setWarningMessage('This will remove all changes since the last time you saved this playlist. Are you sure?');
		setShowWarningMessageModal(true);
		warningCallbackRef.current = handleUndoChanges;
	}

	const confirmDeleteChange = () => {
		setWarningMessage('This action will remove this playlist. Are you sure?');
		setShowWarningMessageModal(true);
		warningCallbackRef.current = () => handlePlaylistRemoval(playlist.id);
	}

	const handleUndoChanges = () => {
		setShowWarningMessageModal(false);
		setPlaylist(initialPlaylist);
		setPlaylistSlides(initialPlaylistSlides);
		setLoop(initialLoop);
		setEnabledButtons(false);
		setStateChanged(false);
	}

	const validateSubmit = () => {
		// Check the location tags for the playlist. Will the user still have access to the playlist?
		const commonLocations = getCommonLocationsBetweenUserPermsAndProduct(playlist.locationTags, permissions, 'playlistFull');
		if (playlist.locationTags.length === 0) {
			// Not associated with any playlist. Might as well delete.
			setShowConfirmLocationChangeModal(true);
			setConfirmLocationChangeMessage('delete');
		} else if (commonLocations.length === 0) {
			// Playlist will be inaccessible to user after saving
			setShowConfirmLocationChangeModal(true);
			setConfirmLocationChangeMessage('lose_access');
		} else if (commonLocations.length > 0) {
			handleSubmit();
		}
	}

	const handleSubmit = async () => {
		const removeSlides = deletedSlides.map(slide => {
			const { slideId, ...ps } = slide;
			return ps;
		});

		const filteredPlaylistSlides = filterAndReorder(playlistSlides);

		// Save playlist slides
		try {
			const updatedPlaylistSlides = await createOrUpdatePlaylistSlide([...filteredPlaylistSlides, ...removeSlides]);
			if (updatedPlaylistSlides) {
				// Only save playlist if slides update succeeded
				const saveResp = await saveContentPlaylist({...playlist, label: playlist.label.trim(), updatedBy: userData.sub});
				if (saveResp.status === 201) {
					setShowSaveMessageModal(true);
					updatePlaylistAndSlideDefinitions();
					setStateChanged(false);
					setShowConfirmLocationChangeModal(false);
					setInitialPlaylist({...playlist, label: playlist.label.trim(), updatedBy: userData.sub});
					setInitialPlaylistSlides(updatedPlaylistSlides);

					let commonLocations = getCommonLocationsBetweenUserPermsAndProduct(playlist.locationTags, permissions, 'playlistFull');
					if (commonLocations.length === 0) {
						setPlaylist({});
						getPlaylists();
					}
				}
			} else {
				setShowErrorMessageModal(true);
				setEnabledButtons(true);
			}
		} catch (err) {
			setShowErrorMessageModal(true);
			setEnabledButtons(true);
		}
	}

	const handlePlaylistRemoval = async () => {
		if (!playlist?.id) return;

		const status = await removePlaylist(playlist?.id);
		//If success, set all values back to the default settings
		if (status?.status === 200) {
			// Modify the contents of the Playlists dropdown
			var tempPlaylists = [];
			playlists.forEach((pl) => {
				if (playlist.id !== pl.playlist.id) {
					tempPlaylists.push(pl);
				}
			});

			setPlaylist({})
			setPlaylistSlides([])
			setInitialPlaylistSlides([])
			setEnabledButtons(false);
			setPlaylists(tempPlaylists);
			setShowConfirmLocationChangeModal(false);
			setStateChanged(false);
			setWarningMessage(null);
			setShowWarningMessageModal(false);
		}
		else {
			setMessage(`Failed to remove playlist)`);
		}
	}

	const removeSlideFromPlaylistSlide = (index) => {
		let tempPlaylistSlides = [...playlistSlides];

		 // Remove the slide at the given index
  		const removedSlide = tempPlaylistSlides.splice(index, 1);

		//If loop is turned off, set the last duration to null
		if (!loop) {
			tempPlaylistSlides = setLoopDuration(tempPlaylistSlides, PLAYLIST_SLIDE_NUMBER);
		}

		setDeletedSlides(prev => [...prev, ...removedSlide]);
		setPlaylistSlides(tempPlaylistSlides);
		setEnabledButtons(true);
		setStateChanged(true);
	}

	const onClickLoop = () => {
		const tempPlaylistSlides = [...playlistSlides];
		//Find the last playlist slide with content
		let lastPs = {};
		for (let i = tempPlaylistSlides.length - 1; i >= 0; i--) {
			if (i === tempPlaylistSlides.length - 1) {
				lastPs = tempPlaylistSlides[i]
			}
			//If this playlist slide has duration set to permanent, set it back to default 10sec
			else if (tempPlaylistSlides[i]?.durationMs === null) {
				tempPlaylistSlides[i].durationMs = 10000
			}
		}
		//If no loop, set the last slide to null so it doesn't repeat 
		if (loop) {			
			lastPs.durationMs = null;
			setPlaylistSlides(tempPlaylistSlides)
		}
		//If loop, set the last slide back to a default duration
		else {
			lastPs.durationMs = 10000;
			setPlaylistSlides(tempPlaylistSlides)
		}	
		setLoop(!loop)
		setEnabledButtons(true);
		setStateChanged(true);
	}

	const filterAndReorder = () => {
		var tempPlaylistSlides = [];
		var toDelete = [];
		var countPlaceholderSlides = 0;
		var expectedSortOrder = 1;
		playlistSlides.forEach((ps, index) => {
			if (!ps.slideId && ps.id) {
				// This slide is going to be deleted
				toDelete.push(ps);
			} else if (ps.slideId) {
				if (ps.sortOrder !== expectedSortOrder) {
					ps.sortOrder = expectedSortOrder;
				}
				tempPlaylistSlides.push(ps);
				expectedSortOrder++;
			} else {
				countPlaceholderSlides++;
			}
		});

		// Modify the toDelete sortOrder. We must avoid a situation where
		// two or more playlistSlides have the same sortOrder, even though
		// they are supposed to be deleted.
		toDelete.forEach((ps) => {
			ps.sortOrder = expectedSortOrder;
			expectedSortOrder++;
			tempPlaylistSlides.push(ps);
		});

		return tempPlaylistSlides;
	}

	const handleDeviceTypeSelect = (option) => {
		const rotation = option.value === 'portrait' ? 90 : 0; // Only available for portrait
		
		setPlaylist({
			...playlist,
			deviceType: option.value,
			rotation: rotation
		});
		setStateChanged(true);
	}

	const handleSlideSelect = (slide) => {
		const tempPlaylistSlides = [...playlistSlides];
		const index = selectedIndexRef.current;

		let durationMs = 10000;
		if (!loop) {
			// Set previous slide's durationMs to default 10000
			if (tempPlaylistSlides.length > 0) {
				tempPlaylistSlides[tempPlaylistSlides.length - 1].durationMs = 10000;
			}
			durationMs = null;
		}

		// Create new playlistSlide object
		const newPlaylistSlide = {
			id: tempPlaylistSlides[index]?.id,
			playlistId: playlist.id,
			slideId: slide.id,
			sortOrder: tempPlaylistSlides.length + 1,
			durationMs: durationMs,
			url: slide?.url,
			label: slide.label,
			showTimer: false,
		};
		tempPlaylistSlides[index] = newPlaylistSlide;

		setPlaylistSlides([...tempPlaylistSlides]);
		setStateChanged(true);
	}

	const openContentModal = async (playlistSlide, index, page, editType) => {
		selectedIndexRef.current = index;
		const slide = playlistSlide?.slideId ? await getSlideById(playlistSlide.slideId) : {};
		const afSlide = isAFSlide(slide?.type);
		handleContentModalOpen(
			afSlide ? 'Actionfigure Elements' : page,
			afSlide ? null : slide,
			afSlide ? null : editType,
			handleSlideSelect,
			playlist.deviceType
		);
	}

	const moveSlideUp = (index) => {
		const tempPlaylistSlides = [...playlistSlides];
		const currentSlide = tempPlaylistSlides[index];
		const prevSlide = tempPlaylistSlides[index - 1];

		// Swap sortOrder
		const tempOrder = currentSlide.sortOrder;
		currentSlide.sortOrder = prevSlide.sortOrder;
		prevSlide.sortOrder = tempOrder;

		// If either slide has a null duration, swap them and set the current slide to 10sec and the previous slide to null to maintain the loop or non-loop behavior
		if (currentSlide.durationMs === null) {
			currentSlide.durationMs = 10000;
			prevSlide.durationMs = null;
		}

		// Swap positions in array
		tempPlaylistSlides[index] = prevSlide;
		tempPlaylistSlides[index - 1] = currentSlide;

		setPlaylistSlides(tempPlaylistSlides);
		setStateChanged(true);
	}

	const moveSlideDown = (index) => {
		const tempPlaylistSlides = [...playlistSlides];
		const currentSlide = tempPlaylistSlides[index];
		const nextSlide = tempPlaylistSlides[index + 1];

		// Swap sortOrder
		const tempOrder = currentSlide.sortOrder;
		currentSlide.sortOrder = nextSlide.sortOrder;
		nextSlide.sortOrder = tempOrder;

		// If either slide has a null duration, swap them and set the current slide to 10sec and the next slide to null to maintain the loop or non-loop behavior
		if (nextSlide.durationMs === null) {
			nextSlide.durationMs = 10000;
			currentSlide.durationMs = null;
		}

		// Swap positions in array
		tempPlaylistSlides[index] = nextSlide;
		tempPlaylistSlides[index + 1] = currentSlide;

		setPlaylistSlides(tempPlaylistSlides);
		setStateChanged(true);
	}

	return (
		<div className="ContentManagementPage">
			{
				showOpenPlaylistModal
				? <OpenPlaylistModal playlists={playlists} selectedPlaylist={playlist} selectPlaylist={selectPlaylist} setShowOpenPlaylistModal={setShowOpenPlaylistModal} />
				: <></>
			}
			{
				showPlaylistModal
				? <PlaylistModal setShowPlaylistModal={setShowPlaylistModal} customerId={customer.id} selectPlaylist={selectPlaylist} setPlaylists={setPlaylists} locations={locations} permissions={permissions} />
				: <></>
			}
			{
				showSaveMessageModal
				? <SaveMessageModal setShowSaveMessageModal={setShowSaveMessageModal} />
				: <></>
			}
			{
				showErrorMessageModal
				? <ErrorMessageModal setShowErrorMessageModal={setShowErrorMessageModal} errorMessage={errorMessage} />
				: <></>
			}
			{
				showWarningMessageModal
				? <WarningMessageModal
					setShowWarningMessageModal={setShowWarningMessageModal}
					warningMessage={warningMessage}
					cb={warningCallbackRef.current} />
				: <></>
			}
			{
				showConfirmLocationChangeModal
				? <ConfirmLocationChangeModal
					message={confirmLocationChangeMessage}
					setShowConfirmLocationChangeModal={setShowConfirmLocationChangeModal}
					handleSubmit={handleSubmit}
					handlePlaylistRemoval={handlePlaylistRemoval} />
				: <></>
			}
			<div className="screen-actions">
                <div className="buttons-and-name">
                    <div className="buttons-container pure-u-1-9">
                        <button className="open-screen" onClick={() => setShowPlaylistModal(true)}>
                            <img className="open-icon" src={plusIcon}/>
                        </button>
                        <button className="open-screen" onClick={() => setShowOpenPlaylistModal(true)} >
                            <img className="open-icon" src={openReport}/>
                        </button>
                    </div>
                    <div className="name-container pure-u-1-7">
                        <div className="name">
							<h4>{playlist?.label || 'No playlist selected'}</h4>
							<p>{`${playlist?.updatedAt ? 'Last updated: ' + formatDate(playlist?.updatedAt) : 'Create or open a playlist to get started'}`}</p>
                        </div>
                    </div>
				</div>
				<div className="screen-tabs">
					<div title="Undo all changes" className={`icon-container ${!playlist?.id || !stateChanged ? "disabled" : ""}`}  onClick={() => confirmUndoChanges()}>
						<ReactSVG className="icon" src={returnIcon} beforeInjection={(svg) => !playlist?.id || !stateChanged ? fillSvgColor(svg, '#CCD2D3') : null}/>
					</div>
					<div title="Save changes" className={`icon-container ${!playlist?.id ? "disabled" : ""} ${stateChanged ? "changed" : ""}`} onClick={!playlist?.id? null : validateSubmit}>
						<ReactSVG className="icon" src={saveIcon} beforeInjection={(svg) => !playlist?.id ? fillSvgColor(svg, null, '#CCD2D3') : stateChanged ? fillSvgColor(svg, null, '#fff') : null} />
					</div>
					<div title="Preview Screen">
						<a href={`${env.LOADER_URL}/${playlist?.shortCode}`} target="_blank" className={`${!playlist?.shortCode || stateChanged ? 'disabled' : ''}`} style={!playlist?.shortCode || stateChanged ? {'cursor': 'default', 'pointerEvents': 'none'} : {}}>
							<div className={`icon-container ${!playlist?.shortCode || stateChanged ? "disabled" : ""}`} title="Preview screen" style={{ cursor: !playlist?.shortCode || stateChanged ? 'default' : 'pointer' }}>
								<ReactSVG className="icon preview-screen-icon" src={eyeIcon} beforeInjection={(svg) => !playlist?.createdAt || stateChanged ? fillSvgColor(svg, '#CCD2D3', '#CCD2D3') : null}/>
							</div>
						</a>
					</div>
					<div className="vertical-line"></div>
					<div title="Delete Playlist" className={`icon-container ${!playlist?.id ? "disabled" : ""}`} onClick={confirmDeleteChange}>
						<ReactSVG className="delete-icon" src={playlist?.id ? deleteIcon : disabledDeleteIcon}  />
					</div>
					<div className="vertical-line"></div>
					<div title="User guide" className={`icon-container  ${!playlist?.id ? "disabled" : ""}`}>
						<a href="https://transitscreen.notion.site/Playlists-v2-2bf18116f9638082aa17d46dce748180" target="_blank"><img className="open-icon" src={questionIcon}/></a>
					</div>
				</div>
			</div>
			<div className="manage-playlist-container" style={{ display: 'flex', gap: '2rem' }}>
				<div className="manage-playlist-left">
					<h2>Manage playlist</h2>
					<div className="input-group">
						<p className="bold-text">Description <span style={{ color: '#FD4A5C' }}>(required)</span></p>
						<p>Add a brief description to help you find this playlist later when assigning it to your Screen or Mobile.</p>
						<input className={`description-input ${!playlist.id ? "disabled" : ""}`} style={{width: '100%'}} type="text" placeholder="Enter description" value={playlist?.label || ''} onChange={(e) => handleDescriptionChange(e.target.value)} disabled={!playlist.id } />
						<div className="input-guideline">
							Maximum 120 characters
						</div>
					</div>
					<div className="input-group">
						<p className="bold-text">Device type</p>
						<p>Choose the type of device to display this playlist on. As you build it, only content that fits the device’s orientation and features will appear.</p>
						<div className="playlist-radio-group">
							{DEVICE_TYPE_OPTIONS.map(option => (
								<label className="playlist-radio-row" key={option.value}>
									<input
										type="radio"
										name="playlist-radio"
										value={option.value}
										checked={playlist.deviceType === option.value}
										onChange={(e) => handleDeviceTypeSelect(option)}
										disabled={!playlist.id}
										style={{cursor: !playlist.id ? 'default' : 'pointer'}}
									/>
									<span className="playlist-radio-btn"></span>
									<img src={option.icon} alt="" className="playlist-radio-icon" />
									<span className="playlist-radio-label">{option.label}</span>
								</label>
							))}
						</div>
					</div>
					<div className="input-group">
						<p className="bold-text">Loop playlist</p>
						<p>Screens usually loop a playlist so it runs continuously. On mobile, the playlist should play once and end on the slide you want users to see, like TransitScreen or Nearby.</p>
						<label className="loop-checkbox-row">
							<input
								type="checkbox"
								checked={!playlist.id ? false : loop}
								onChange={onClickLoop}
								disabled={!playlist.id}
								style={{cursor: !playlist.id ? 'default' : 'pointer'}}
							/>
							<span className={`loop-checkbox-custom ${!playlist.id ? "disabled" : ""}`}></span>
							<span className="loop-checkbox-label">Loop this playlist continuously</span>
						</label>
					</div>
					<div className="input-group">
						<p className="bold-text">Sharing</p>
						<p>You can share this playlist. Choose any location(s) where you would like it to be available. You must choose at least one.</p>
						<div className="sharing-checkbox-group">
							{locations && Object.entries(locations).map(([mainKey, subLocations]) => {
								const allSubIds = subLocations.map(loc => loc.id);
								const mainChecked = allSubIds.length > 0 && allSubIds.every(id => (playlist?.locationTags || []).includes(id));
								return (
									<div key={mainKey} className="sharing-main-group">
										<label className="main-checkbox">
											<input
												type="checkbox"
												value={mainKey}
												checked={mainChecked}
												disabled={!playlist.id}
												onChange={() => {
													setPlaylist(prev => {
														const tags = prev?.locationTags || [];
														let newTags;
														if (mainChecked) {
															// Remove all sub ids
															newTags = tags.filter(t => !allSubIds.includes(t));
														} else {
															// Add all sub ids (avoid duplicates)
															newTags = Array.from(new Set([...tags, ...allSubIds]));
														}
														return { ...prev, locationTags: newTags };
													});
													setStateChanged(true);
												}}
											/>
											{replaceUnderscore(mainKey, ', ')}
										</label>
										{Array.isArray(subLocations) && subLocations.length > 0 && (
											<div className="sharing-sub-group">
												{subLocations.map((subLoc, j) => {
													const subChecked = (playlist?.locationTags || []).includes(subLoc.id);
													return (
														<label key={subLoc.name + subLoc.id} className="sub-checkbox">
															<input
																type="checkbox"
																value={subLoc.id}
																checked={subChecked}
																disabled={!playlist.id}
																onChange={() => {
																	setPlaylist(prev => {
																		const tags = prev?.locationTags || [];
																		const exists = tags.includes(subLoc.id);
																		return {
																			...prev,
																			locationTags: exists
																				? tags.filter(t => t !== subLoc.id)
																				: [...tags, subLoc.id]
																		};
																	});
																	setStateChanged(true);
																}}
															/>
															{subLoc.name}
														</label>
													);
												})}
											</div>
										)}
									</div>
								);
							})}
						</div>
					</div>
				</div>
				<div className="manage-playlist-right">
					<h3>Build your playlist</h3>
					<p>This is where you build your playlist. You can add as many slides as you like, assign content to them from your content library and then set various configuration options per slide.</p>
					{
						playlist?.id
						? <div className="slide-select-container">
							{
								playlistSlides.map((ps, index) => {
									const slideImage = ps?.url === 'transit-placeholder' || ps?.slideId === 'd8431f1b-aca5-458c-8ff2-0a4ff118a9ac'
									? <IFrame url={transitTile} type={'image'} /> 
									: ps?.url === 'nearby-placeholder' || ps?.slideId === 'c7a3d0a6-0452-4e50-96fe-c8e71b9f49d0'
									? <IFrame url={nearbyTile} type={'image'} /> 
									: (
										// Is there a playlistSlide URL? Determine if it's image hosted in S3 or an external link
										ps?.url
										? ps.url.includes(env.S3_MESSAGING_BUCKET) ? <IFrame url={ps.url} type={'image'} /> : ps.url.includes(env.MESSAGE_LOADER_URL) ? <IFrame url={ps.url} type={'url'} /> : getLinkTile(ps, 'droppable-link')
										: <div className="placeholder-image" key={index}>
											<img src={dragIcon} />
											<div>Add a new slide</div>
										</div>
									)

									return <PlaylistSlideItem key={index} playlistSlide={ps} index={index} slideImage={slideImage} displayDurationOption={displayDurationOption} selectDurationMs={selectDurationMs} setCountdownIndicator={setCountdownIndicator} removeSlideFromPlaylistSlide={removeSlideFromPlaylistSlide} openContentModal={openContentModal} moveSlideUp={moveSlideUp} moveSlideDown={moveSlideDown} playlistSlideLength={playlistSlides.length} permissions={permissions} />
								})
							}
							<PlaylistSlideItem key="add-slide" isAddSlide={true} openContentModal={openContentModal} index={playlistSlides.length} permissions={permissions} />
						</div>
						: <div className="start-selection">
							<div className="start-icon-container">
								<div className="start-icon">
									<img className="open-icon" src={plusIcon}/>
								</div>
								<div className="start-icon">
									<img className="open-icon" src={openReport}/>
								</div>
							</div>
							<h1 style={{color: '#2386EE'}}>Let's get started!</h1>
							<p style={{width: '50%', marginLeft: '2em'}}>Use these buttons in the top navigation bar to create a new playlist or open an existing one.</p>
						</div>
					}
				</div>
			</div>
		</div>
	)
}

const IFrame = ({ url, type }) => {
	const ref = useRef();

	return type === 'image' 
		? <img src={url} ref={ref} className="slide-content" onError={
            e => { 
                e.target.onerror = null; 
                e.target.src = errorIcon; 
                e.target.className = "clm-slide-image-error";
            }} 
		/> 
		: <iframe src={url} ref={ref} className="slide-content"></iframe>
}

const PlaylistSlideItem = ({
	playlistSlide,
	index,
	slideImage,
	displayDurationOption,
	selectDurationMs,
	setCountdownIndicator,
	removeSlideFromPlaylistSlide,
	isAddSlide,
	openContentModal,
	moveSlideUp,
	moveSlideDown,
	playlistSlideLength,
	permissions
}) => {

	return (
		<div className="slide-item" key={index}>
			{
				isAddSlide
				? <>
					<div
						className={`slide-image-section ${ userHasPermission('contentLibrary', permissions) ? '' : 'disabled' }`}
						onClick={ userHasPermission('contentLibrary', permissions) ? () => openContentModal(playlistSlide, index, 'shared_folder') : null}
					>
						<div className="placeholder-image">
							<img src={dragIcon} />
							<div>Add a new slide</div>
						</div>
					</div>
					<div className="slide-content-section"></div>
					<div className="slide-buttons-section"></div>
				</>
				: <>
					<div className="slide-image-section" onClick={() => openContentModal(playlistSlide, index, 'shared_folder', 'edit')}>
						{slideImage}
					</div>
					<div className="slide-content-section">
						<h2>Slide {index + 1}</h2>
						<div className="slide-input-container">
							<p className="label">Description:</p>
							<p>{playlistSlide.label}</p>
						</div>
						<div className="slide-input-container">
							<p className="label" style={{ marginTop: '0.5em' }}>Display for:</p>
							<Select
								isDisabled={playlistSlide?.slideId ? '': 'disabled'}
								onChange={(e) => selectDurationMs(e.value, index)}
								options={DURATION_OPTIONS}
								value={displayDurationOption(playlistSlide?.durationMs)}
								isOptionDisabled={option => option.disabled}
								className="duration-dropdown"
								maxMenuHeight={100}
								styles={{ container: (base) => ({ ...base, width: '50%' }) }}
							/>
						</div>
						<div className="slide-input-container">
							<p className="label">Countdown timer:</p>
							<div className="switch-row">
								<Switch
									className="switch"
									checked={playlistSlide?.showTimer || false}
									onChange={checked => setCountdownIndicator(checked, index)}
									onColor="#2386EE"
									uncheckedIcon
									checkedIcon={false}
									disabled={!playlistSlide?.slideId ? true : false}
								/>
							</div>
						</div>
					</div>
					<div className="slide-buttons-section">
						<div className="slide-button remove-slide" onClick={() => removeSlideFromPlaylistSlide(index)}>x</div>
						<div className="slide-buttons-bottom">
							<div className="slide-button arrow-button" onClick={() => index === 0 ? null : moveSlideUp(index)}><img src={upCaret} alt="Move Up" /></div>
							<div className="slide-button arrow-button" onClick={() => index === playlistSlideLength - 1 ? null : moveSlideDown(index)}><img src={downCaret} alt="Move Down" /></div>
						</div>
					</div>
				</>
			}
		</div>
	)
};

export function PlaylistModal({ setShowPlaylistModal, customerId, selectPlaylist, setPlaylists, locations, permissions }) {

	const [playlistName, setplaylistName] = useState('');

	const locationIds = locations ? Object.values(locations).flat().map(loc => loc.id) : [];

	const handlePlaylistCreation = async () => {
		const body = {
			customerId,
			label: playlistName,
			access: 'public',
			deviceType: 'landscape',
			type: 'content',
			product: 'screen',
			locationTags: locationIds
		}
		await createPlaylist(body)
		.then(async data => {

			await getPlaylistData(customerId, permissions['playlistFull'])
			.then(playlists => {
				setPlaylists(playlists);
				//Find the BIZ playlist to update current DASH2 data by
				const updatedPlaylist = playlists.find(pl => pl.playlist.id === data?.id);
				selectPlaylist(updatedPlaylist)
			})
			.catch((error) => {
				console.error('Something went wrong:', error);
			});
			setShowPlaylistModal(false);
		})		
	}

	return <div className="Modal">
		<div className="ModalWindow CreatePlaylistModal">
			<div className="input-container">
				<h2>Create a playlist</h2>
				<p>Add a brief description to help you find this playlist later when assigning it to your Screen or Mobile.</p>
				<div className="modal-input">
					<input style={{border:'none'}} value={playlistName} onChange={e => setplaylistName(e.target.value)} placeholder="Give this playlist an easy to remember name." maxLength={"120"}></input>
				</div>
			</div>
			<div style={{display: "flex",  justifyContent: "flex-end", color: "#b7bfc1"}}>Maximum 120 characters</div>
			<div className="buttons-container">
				<button className="secondary-button" onClick={() => setShowPlaylistModal(false)} >Cancel</button>
				<button className={playlistName === '' ? "secondary-button disabled-button" : "primary-button"} onClick={() => handlePlaylistCreation()} disabled={playlistName === '' ? true : false}>Create</button>
			</div>
		</div>
	</div>
}


const SaveMessageModal = ({setShowSaveMessageModal}) => {
	return <div className="Modal">
		<div className="ModalWindow playlist-crud-message">
			Your updates have been saved.
			<div className="buttons-container" style={{justifyContent: "flex-end"}}>
				<button className="primary-button" onClick={() => setShowSaveMessageModal(false)}>Okay</button>
			</div>
		</div>
	</div>
};

const ErrorMessageModal = ({setShowErrorMessageModal, errorMessage}) => {
	return <div className="Modal">
		<div className="ModalWindow playlist-crud-message" style={{justifyContent:"center"}}>
			<span style={{color: "red"}}>{ errorMessage ?? "Error saving playlist. Please try again."}</span>
			<div className="buttons-container">
				<button className="primary-button" onClick={() => setShowErrorMessageModal(false)}>Okay</button>
			</div>
		</div>
	</div>
};

const WarningMessageModal = ({setShowWarningMessageModal, warningMessage, cb}) => {
	return <div className="Modal">
		<div className="ModalWindow playlist-crud-message">
			{ warningMessage }
			<div className="buttons-container">
				<button className="secondary-button" onClick={() => setShowWarningMessageModal(false)}>Cancel</button>
				&nbsp;
				<button className="primary-button" onClick={() => cb()}>Ok</button>
			</div>
		</div>
	</div>
};

const OpenPlaylistModal = ({ playlists, selectedPlaylist, selectPlaylist, setShowOpenPlaylistModal }) => {

	const [filteredPlaylists, setFilteredPlaylists] = useState([...playlists]);
	const [highlightedPlaylist, setHighlightedPlaylist] = useState(selectedPlaylist);
	const [sortKey, setSortKey] = useState('label');
	const [sortDirection, setSortDirection] = useState('asc');

	const sortPlaylists = (playlists, key, direction) => {
		return [...playlists].sort((a, b) => {
			const aVal = a.playlist[key] ?? '';
			const bVal = b.playlist[key] ?? '';
			if (aVal < bVal) return direction === 'asc' ? -1 : 1;
			if (aVal > bVal) return direction === 'asc' ? 1 : -1;
			return 0;
		});
	}

	function handleSort(key) {
		if (sortKey === key) {
			setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
		} else {
			setSortKey(key);
			setSortDirection('asc');
		}
	}

	const playlistRows = (filteredPlaylists).length === 0
	? <tr className="no-playlists-text"><td colSpan="4">No playlists found</td></tr>
	: sortPlaylists(filteredPlaylists, sortKey, sortDirection).map(pl => {
		const playlist = pl?.playlist;

		// Select icon based on deviceType
		let deviceIcon = playlist?.deviceType === 'landscape' ? landscapeIcon
			: playlist?.deviceType === 'portrait' ? portraitIcon
			: playlist?.deviceType === 'mobile' ? mobileIcon
			: null;

		return <tr key={playlist?.id} className={`playlist-row ${playlist?.id === highlightedPlaylist?.id ? 'selected' : ''}`} onClick={() =>setHighlightedPlaylist(playlist)}>
			<td>{playlist?.shortCode}</td>
			<td>{truncateLabel(playlist?.label)}</td>
			<td className="device-type-cell">
				{deviceIcon && <img src={deviceIcon} alt={playlist?.deviceType} />}
				<span className="device-type-text">{capitalizeFirst(playlist?.deviceType)}</span>
			</td>
			<td>{playlist?.updatedBy}</td>
			<td>{formatDate(playlist?.updatedAt)}</td>
		</tr>
	})

	const handleOpen = () => {
		const selectedPlaylist = playlists.find(pl => pl.playlist.id === highlightedPlaylist.id);
		selectPlaylist(selectedPlaylist);
		setShowOpenPlaylistModal(false);
	}

	return (
		 <div className="modal-wrapper">
			<div className="PlaylistModal">
				<div className="ModalWindow custom-modal">
					<div className="modal-header">
						<h3>Open a playlist</h3>
					</div>
					<div className="table-container">
						<table className="playlist-table">
							<tbody>
								<tr className="header-row">
									<th className="table-header" onClick={() => handleSort('shortCode')}>Code</th>
									<th className="table-header" onClick={() => handleSort('label')}>Name</th>
									<th className="table-header" onClick={() => handleSort('deviceType')}>Device type</th>
									<th className="table-header" onClick={() => handleSort('updatedBy')}>Last updated by</th>
									<th className="table-header" onClick={() => handleSort('updatedAt')}>Updated</th>
								</tr>
								{playlistRows}
							</tbody>
						</table>
					</div>
					<div className="buttons-container">
						<button className="secondary-button" onClick={() => setShowOpenPlaylistModal(false)}>Close</button>
						<button className="primary-button" onClick={handleOpen}>Open</button>
					</div>
				</div>
			</div>
		</div>
	);
}

const ConfirmLocationChangeModal = ({
	message,
	setShowConfirmLocationChangeModal,
	handleSubmit,
	handlePlaylistRemoval
}) => {
	return <div className="Modal">
		<div className="ModalWindow playlist-crud-message">
			{
				message === 'delete'
				? 'This playlist is not shared with any location within your organization. If you proceed, it will be deleted. This action cannot be undone. Do you wish to continue?'
				: 'This playlist is not shared with any location you can access. If you proceed, you will lose access until someone with access shares it to one of your locations. Do you wish to continue?'
			}
			<div className="buttons-container">
				<button className="secondary-button" onClick={() => setShowConfirmLocationChangeModal(false)}>Cancel</button>
				&nbsp;
				<button
					className={message === 'lose_access' ? 'primary-button' : 'delete-button-red-bg'}
					onClick={message === 'lose_access' ? () => handleSubmit() : () => handlePlaylistRemoval()}>
						{ message === 'lose_access' ? 'OK' : 'Delete' }
					</button>
			</div>
		</div>
	</div>
}

export default ContentManagementPage
